import { Link } from "react-router-dom";

import { useConfirm } from "../confirm/context";
import { Pill } from "../Pill";
import type { CampaignMembership } from "../../types";

interface Props {
  memberships: CampaignMembership[];
  contactName: string;
  unenrolling: boolean;
  onUnenroll: (slug: string) => void;
}

export function CampaignMemberships({
  memberships,
  contactName,
  unenrolling,
  onUnenroll,
}: Props) {
  const confirm = useConfirm();
  return (
    <div className="panel panel--flush">
      <div className="panel-header">
        <h2>Campaign memberships</h2>
        <span className="text-xs text-ink-low">history – the campaign console shows today's state</span>
      </div>

      {memberships.length === 0 ? (
        <div className="empty-state">
          <div className="title">Not enrolled</div>
          <p className="hint">This contact is not in any campaign yet.</p>
        </div>
      ) : (
        <ul
          className="divide-y"
          style={{ borderColor: "var(--c-rule)" }}
        >
          {memberships.map((m) => (
            <li key={m.slug} className="px-6 py-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <Link to={`/campaigns/${m.slug}`} className="font-heading font-semibold text-ink hover:text-accent">
                  {m.slug}
                </Link>
                <div className="flex items-center gap-2">
                  <Pill value={m.status} />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title="Remove from campaign"
                    disabled={unenrolling}
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Remove ${contactName} from campaign "${m.slug}"?`,
                        confirmLabel: "Remove",
                        tone: "danger",
                      });
                      if (ok) onUnenroll(m.slug);
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="text-xs text-ink-low mb-3">
                {m.enrolled_at
                  ? `enrolled ${m.enrolled_at.slice(0, 10)}`
                  : ""}
              </div>
              {m.steps && m.steps.length > 0 ? (
                <ol className="space-y-1.5 pl-0">
                  {m.steps.map((s, idx) => (
                    <li
                      key={s.step_id}
                      className="flex items-baseline gap-3 text-sm"
                    >
                      <span className="tnum text-xs text-ink-low w-6 shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="text-ink-medium">
                        {s.step_id}
                      </span>
                      <Pill value={s.status} />
                      {s.completed_at ? (
                        <span className="text-xs text-ink-low ml-auto">
                          done {s.completed_at.slice(0, 10)}
                        </span>
                      ) : s.prepared_at ? (
                        <span className="text-xs text-ink-low ml-auto">
                          prepared {s.prepared_at.slice(0, 10)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ol>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
